import { useEffect, useState } from "react";

// Petit panneau de tuning visuel pour le placement des tuiles de décor.
// Activé uniquement si l'URL contient ?debugDecor=1.
//
// Affiche un input numérique pour ajuster le "nudge" appliqué à l'espacement
// inter-tuile : nudge négatif → tuiles qui se chevauchent (corrige un gap),
// nudge positif → écart supplémentaire. Valeur persistée en localStorage pour
// survivre aux refresh.

const STORAGE_KEY = "decor.tileGapNudge";

export function useTileGapNudge(): number {
  const [nudge, setNudge] = useState<number>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? parseFloat(stored) || 0 : 0;
  });

  useEffect(() => {
    const onUpdate = (e: Event) => {
      const ce = e as CustomEvent<number>;
      setNudge(ce.detail);
    };
    window.addEventListener("decor-nudge-changed", onUpdate);
    return () => window.removeEventListener("decor-nudge-changed", onUpdate);
  }, []);

  return nudge;
}

interface DebugPanelProps {
  tileLengthHint?: number; // pour afficher la longueur native de la tuile en info
}

export function DecorDebugPanel({ tileLengthHint }: DebugPanelProps) {
  const [nudge, setNudge] = useState<number>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? parseFloat(stored) || 0 : 0;
  });

  const updateNudge = (n: number) => {
    setNudge(n);
    localStorage.setItem(STORAGE_KEY, String(n));
    window.dispatchEvent(new CustomEvent("decor-nudge-changed", { detail: n }));
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 12,
        right: 12,
        zIndex: 9999,
        background: "rgba(0,0,0,0.78)",
        color: "#fff",
        padding: "10px 12px",
        borderRadius: 8,
        fontFamily: "ui-monospace, Menlo, monospace",
        fontSize: 12,
        lineHeight: 1.4,
        display: "flex",
        flexDirection: "column",
        gap: 6,
        minWidth: 220,
      }}
    >
      <div style={{ fontWeight: 700, opacity: 0.85 }}>Decor tile gap nudge</div>
      <div style={{ opacity: 0.7 }}>
        Δ &lt; 0 = overlap · Δ &gt; 0 = écart
      </div>
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <button onClick={() => updateNudge(nudge - 0.5)} style={btnStyle}>−0.5</button>
        <button onClick={() => updateNudge(nudge - 0.1)} style={btnStyle}>−0.1</button>
        <button onClick={() => updateNudge(nudge - 0.01)} style={btnStyle}>−0.01</button>
        <button onClick={() => updateNudge(0)} style={{ ...btnStyle, background: "#555" }}>0</button>
        <button onClick={() => updateNudge(nudge + 0.01)} style={btnStyle}>+0.01</button>
        <button onClick={() => updateNudge(nudge + 0.1)} style={btnStyle}>+0.1</button>
        <button onClick={() => updateNudge(nudge + 0.5)} style={btnStyle}>+0.5</button>
      </div>
      <input
        type="number"
        step="0.01"
        value={nudge}
        onChange={(e) => updateNudge(parseFloat(e.target.value) || 0)}
        style={{
          background: "#111",
          color: "#fff",
          border: "1px solid #333",
          padding: "4px 6px",
          borderRadius: 4,
          width: "100%",
          fontFamily: "inherit",
          fontSize: 13,
        }}
      />
      <div
        style={{
          marginTop: 4,
          padding: "8px 10px",
          background: "#1a1a1a",
          border: "1px solid #444",
          borderRadius: 6,
          fontSize: 11,
          lineHeight: 1.5,
        }}
      >
        <div style={{ opacity: 0.7 }}>Tile natif (BBox×scale)</div>
        <div style={{ fontSize: 14, fontWeight: 600 }}>
          {tileLengthHint !== undefined ? tileLengthHint.toFixed(4) : "…chargement"}
        </div>
        <div style={{ opacity: 0.7, marginTop: 4 }}>Écart effectif (= natif + nudge)</div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 16,
            fontWeight: 700,
            color: "#7dd3fc",
          }}
        >
          <span>
            {tileLengthHint !== undefined
              ? (tileLengthHint + nudge).toFixed(4)
              : "—"}
          </span>
          {tileLengthHint !== undefined && (
            <button
              onClick={() =>
                navigator.clipboard?.writeText(
                  (tileLengthHint + nudge).toFixed(4)
                )
              }
              style={{ ...btnStyle, padding: "2px 6px", fontSize: 10 }}
              title="Copier la valeur"
            >
              copy
            </button>
          )}
        </div>
        <div style={{ opacity: 0.55, marginTop: 4, fontSize: 10 }}>
          Nudge actuel : {nudge.toFixed(4)}
        </div>
      </div>
    </div>
  );
}

const btnStyle: React.CSSProperties = {
  background: "#222",
  color: "#fff",
  border: "1px solid #444",
  borderRadius: 4,
  padding: "3px 6px",
  cursor: "pointer",
  fontFamily: "inherit",
  fontSize: 11,
};
